"use client";

import { cn } from "@/lib/utils";
import { Fragment, useMemo } from "react";

/**
 * Key Numbers 1039 — BDU Programme key content.
 *
 * Port of BDU v1 `components/Degree/Highlights.tsx`: a solid bar of big-number +
 * label columns. Distinct from Key Numbers 1036 (icon + one line of copy) and from
 * Key Numbers 1026 (the home Stats bar, which circles its icons).
 *
 * SEPARATORS — the reference draws a vertical hairline before every column except
 * the first, but below 768px only before ODD indices (so it sits between the two
 * columns of the mobile grid), plus a horizontal hairline after the second item.
 * Both rules are reproduced.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 */

interface ContentRow {
  id?: string | number;
  /** The number itself, e.g. "100+" */
  meta_title?: string;
  /** The label under it, e.g. "Live Lectures" */
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface KeyNumbers1039Data {
  metadata?: ContentRow[];
  /** Bar background */
  background_color?: string;
  /** Number colour */
  title_start_color?: string;
  /** Label colour */
  description_color?: string;
  /** Hairline separators */
  gradient_color_4?: string;
  status?: boolean;
}

const KeyNumbers1039 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as KeyNumbers1039Data | undefined;

  const {
    background_color = "#836537",
    title_start_color = "#FFFFFF",
    description_color = "#FFFFFF",
    gradient_color_4 = "#FFFFFF66",
  } = sectionData || {};

  const items = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!sectionData?.status || items.length === 0) return null;

  const isOddCount = items.length % 2 !== 0;

  return (
    <div
      className={cn(
        "grid w-full grid-cols-2 items-center justify-center gap-x-11 gap-y-1 px-5 py-8",
        "tab:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] tab:gap-x-8 tab:gap-y-8 tab:px-6 tab:py-6",
      )}
      style={{ backgroundColor: background_color }}
    >
      {items.map((item, index) => (
        <Fragment key={item.id ?? index}>
          <div
            className={cn(
              "flex flex-row items-center justify-center gap-x-[22px] tab:gap-8",
              // An odd final item spans both mobile columns and centres itself.
              isOddCount &&
                index === items.length - 1 &&
                "col-span-2 mx-auto max-w-[400px] tab:col-auto tab:mx-0 tab:max-w-none",
            )}
          >
            {index > 0 && (
              <div
                className={cn(
                  "my-auto hidden h-24 w-px",
                  // Below 768px only the right-hand column of each row gets a rule.
                  index % 2 === 1 && "-ml-[26px] block",
                  "tab:ml-0 tab:block",
                )}
                style={{ backgroundColor: gradient_color_4 }}
              />
            )}
            <div className="flex flex-1 flex-col items-center">
              <p
                className="text-center text-[28px] font-semibold tab:text-4xl"
                style={{ color: title_start_color }}
              >
                {item.meta_title}
              </p>
              <p
                className="text-center text-sm tab:text-base"
                style={{ color: description_color }}
              >
                {item.meta_description}
              </p>
            </div>
          </div>

          {index === 1 && (
            <div
              className="col-span-2 h-px w-1/2 justify-self-center tab:hidden"
              style={{ backgroundColor: gradient_color_4 }}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default KeyNumbers1039;
